/**
 * Proxy Resolution Settings
 *
 * Global preview quality setting. Full (720p) is the default for better quality.
 * Users can switch to Standard (480p) for faster loading on slower connections.
 */

import { useState, useEffect } from 'react'

export type ProxyResolution = '720p' | '520p' | '480p'
export type PreviewQuality = 'standard' | 'full'

// Storage key for global quality setting
export const PREVIEW_QUALITY_KEY = 'martini-preview-quality'
export const QUALITY_CHANGED_EVENT = 'preview-quality-changed'

// Default quality
const DEFAULT_QUALITY: PreviewQuality = 'full'

// Quality to resolution mapping
const QUALITY_TO_RESOLUTION: Record<PreviewQuality, ProxyResolution> = {
  standard: '480p',
  full: '720p',
}

// Cached value to avoid repeated localStorage reads in hot paths
let cachedQuality: PreviewQuality | null = null

/**
 * Get the current preview quality setting.
 */
export function getPreviewQuality(): PreviewQuality {
  if (typeof window === 'undefined') return DEFAULT_QUALITY

  if (cachedQuality !== null) return cachedQuality

  const stored = localStorage.getItem(PREVIEW_QUALITY_KEY)
  if (stored === 'standard' || stored === 'full') {
    cachedQuality = stored
  } else {
    cachedQuality = DEFAULT_QUALITY
  }
  return cachedQuality
}

/**
 * Set the preview quality.
 */
export function setPreviewQuality(quality: PreviewQuality): void {
  if (typeof window === 'undefined') return

  cachedQuality = quality
  localStorage.setItem(PREVIEW_QUALITY_KEY, quality)

  // Dispatch event so components can react
  window.dispatchEvent(new CustomEvent(QUALITY_CHANGED_EVENT, { detail: quality }))
}

/**
 * Get the currently selected proxy resolution based on quality setting.
 * Uses in-memory cache for efficiency - safe to call in hot paths.
 */
export function getProxyResolution(): ProxyResolution {
  const quality = getPreviewQuality()
  return QUALITY_TO_RESOLUTION[quality]
}

/**
 * Transform a proxy URI to use the selected resolution.
 *
 * Proxy URLs follow the pattern: {assetId}-{resolution}-proxy.mp4
 * e.g., abc123-720p-proxy.mp4 -> abc123-480p-proxy.mp4
 *
 * @param proxyUri - The original proxy URI (typically 720p)
 * @param targetResolution - The resolution to switch to (defaults to current setting)
 * @returns The transformed proxy URI, or original if pattern doesn't match
 */
export function transformProxyUri(proxyUri: string, targetResolution?: ProxyResolution): string {
  const resolution = targetResolution ?? getProxyResolution()

  // If already at target resolution, return as-is
  if (proxyUri.includes(`-${resolution}-proxy.mp4`)) {
    return proxyUri
  }

  // Try to match and replace the resolution pattern
  const proxyPattern = /-(\d+p)-proxy\.mp4$/
  if (proxyPattern.test(proxyUri)) {
    return proxyUri.replace(proxyPattern, `-${resolution}-proxy.mp4`)
  }

  // Fallback: return original URI if pattern doesn't match
  // (handles legacy format or non-proxy URIs)
  return proxyUri
}

/**
 * Get the effective proxy URI for an asset, applying resolution transformation.
 * Use this instead of directly accessing asset.proxyUri.
 *
 * @param proxyUri - The asset's proxyUri field
 * @param fallbackUri - The asset's uri field (original video)
 * @returns The resolved URI with correct resolution applied
 */
export function getEffectiveProxyUri(
  proxyUri: string | undefined,
  fallbackUri: string | undefined,
): string | undefined {
  const rawUri = proxyUri || fallbackUri
  if (!rawUri) return undefined

  // Only transform if we have a proxy URI
  if (proxyUri) {
    return transformProxyUri(proxyUri)
  }

  return rawUri
}

/**
 * React hook for reactive proxy resolution.
 * Triggers re-render when resolution changes (quality change or cross-tab).
 * Use this in components/hooks that need to react to resolution changes.
 */
export function useProxyResolution(): ProxyResolution {
  const [resolution, setResolution] = useState<ProxyResolution>(() => getProxyResolution())

  useEffect(() => {
    const handleChange = () => {
      setResolution(getProxyResolution())
    }

    // Listen for quality changes in same tab
    window.addEventListener(QUALITY_CHANGED_EVENT, handleChange)
    // Listen for changes from other tabs
    const handleStorage = (e: StorageEvent) => {
      if (e.key === PREVIEW_QUALITY_KEY) {
        handleChange()
      }
    }
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener(QUALITY_CHANGED_EVENT, handleChange)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  return resolution
}
